import { Flex, Text, useBoolean } from "@chakra-ui/react";
import { documentItemApi } from "apiClient/v2";
import { message } from "components/base";
import IconBlackBoard from "components/icon/IconBlackBoard";
import IconCamera from "components/icon/IconCamera";
import IconEye from "components/icon/IconEye";
import IconThreeDot from "components/icon/IconThreeDot";
import IconTrash from "components/icon/IconTrash";
import { SvgIcon } from "components/SvgIcon";
import CustomMenu from "components/ui/CustomMenu";
import { TYPE_USER } from "constants/app";
import { SubItemKey } from "constants/enum";
import { DocumentCategoryDTO } from "interfaces/dtos/documentCategoryDTO";
import {
  DocumentItemDTO,
  DocumentSubItemDTO,
} from "interfaces/dtos/documentItemDTO";
import { DocumentItem } from "interfaces/models/documentItem";
import { User } from "interfaces/models/user";
import { GetContentLog } from "models/dataLog";
import { updateDocumentCategoriesWhenHidePhoto } from "models/documentCategory";
import { useForgeViewerContext } from "pages/forge-viewer/ForgeViewerContext";
import { memo, useCallback, useMemo } from "react";
import { useDispatch } from "react-redux";
import { updateDocumentItem } from "redux/documentSlice";
import { updateElementInArray } from "utils/array";
import { uuid } from "utils/common";
import {
  checkDocumentItemUpdatedData,
  getStatusVisibleOfItem,
} from "utils/documentItem";
import ConfirmDeleteItemModal from "./ConfirmDeleteItemModal";

interface Props {
  isOnline: boolean;
  subItem: DocumentSubItemDTO;
  documentCategories: DocumentCategoryDTO[];
  documentCategorySelected?: DocumentCategoryDTO;
  documentItemSelected?: DocumentItemDTO;
  currentUser?: User | null;
  isHasBlackboard: boolean;
  isDisabled?: boolean;
  itemDisplayOrder: number | string;
  insertItemLog: (params: GetContentLog) => Promise<void>;
  onSelectSubItem: (subItem: DocumentSubItemDTO) => void;
}

const SubItem = (props: Props) => {
  const {
    isOnline,
    subItem,
    currentUser,
    documentItemSelected,
    isHasBlackboard,
    isDisabled,
    documentCategories,
    insertItemLog,
    onSelectSubItem,
    itemDisplayOrder,
  } = props;
  const { socket } = useForgeViewerContext();
  const [isDeletingSubItem, setIsDeletingSubItem] = useBoolean();
  const [isOpenConfirmDeleteItem, setIsOpenConfirmDeleteItem] = useBoolean();

  const dispatch = useDispatch();

  const isShowSubItem = useMemo(() => !subItem?.isHidden, [subItem?.isHidden]);

  const noSubItem = useMemo(
    () =>
      `${itemDisplayOrder}-${
        (documentItemSelected?.subItems?.findIndex(
          (sub) => sub.id === subItem?.id
        ) || 0) + 1
      }`,
    [subItem.id, itemDisplayOrder, documentItemSelected?.subItems]
  );

  const canDeleteDocumentItem = useMemo(() => {
    if (currentUser?.role === TYPE_USER.ROLE_PARTNER_LEADER) return false;

    const takasagoRoles = Object.values(TYPE_USER);

    return takasagoRoles?.includes(
      currentUser?.role as (typeof TYPE_USER)[keyof typeof TYPE_USER]
    );
  }, [currentUser?.role]);

  const handleAddLogTypeShowHidePin = useCallback(
    (params: {
      documentItem: DocumentItemDTO;
      requestId?: string;
      documentCategory: DocumentCategoryDTO | undefined;
    }) => {
      const { documentCategory, documentItem, requestId } = params;
      if (!documentItem.subItems?.length) {
        return;
      }

      const setSelectedExternalIds = new Set(
        documentCategory?.selectedExternalIds || []
      );
      const isSelectedExternalId = setSelectedExternalIds.has(documentItem.id);

      const { isShowAllItem, isHideAllItem } =
        getStatusVisibleOfItem(documentItem);
      if (
        (isHideAllItem && !isSelectedExternalId) ||
        (isShowAllItem && isSelectedExternalId)
      ) {
        return;
      }

      if (isHideAllItem || isShowAllItem) {
        documentItemApi.updateItem({
          id: documentItem.id,
          isHidePin: isHideAllItem,
          requestId,
        });

        insertItemLog({ field: "isHidePin", value: isHideAllItem, requestId });
      }
    },
    [insertItemLog]
  );

  const handleChangeShowSubItem = useCallback(async () => {
    if (!documentItemSelected?.id || (!isShowSubItem && isDisabled)) {
      return;
    }

    const requestId = uuid();
    const documentItem = structuredClone(documentItemSelected);
    const documentCategory = documentCategories.find(
      (cate) => cate.id === documentItemSelected.documentCategoryId
    );
    const now = new Date();
    updateElementInArray({
      array: documentItem?.subItems || [],
      keyIndex: SubItemKey.ID,
      element: {
        ...subItem,
        isHidden: isShowSubItem,
        updatedAt: now,
      } as DocumentSubItemDTO,
    });

    dispatch(
      updateDocumentItem({ ...documentItem, isUpdateVisibleItem: true })
    );
    updateDocumentCategoriesWhenHidePhoto({
      documentCategories,
      documentItem,
      requestId,
    });

    // add log type show/hide pin
    handleAddLogTypeShowHidePin({ documentCategory, documentItem, requestId });

    const response = await documentItemApi.updateSubItem({
      id: subItem.id,
      itemId: subItem.itemId,
      isHidden: isShowSubItem,
      requestId,
      updatedAt: now,
    } as DocumentSubItemDTO);

    socket.updateSubItem(documentItem, subItem, {
      isHidden: isShowSubItem,
      isUpdateVisibleItem: true,
      updatedAt: response.data.updatedAt,
    });
  }, [
    documentCategories,
    isShowSubItem,
    documentItemSelected,
    subItem,
    isDisabled,
    dispatch,
    handleAddLogTypeShowHidePin,
    socket,
  ]);

  const handleDeleteSubItem = useCallback(async () => {
    if (!documentItemSelected?.id) {
      return;
    }

    setIsDeletingSubItem.on();
    const documentItem = structuredClone(documentItemSelected);
    const documentCategory = documentCategories.find(
      (cate) => cate.id === documentItemSelected.documentCategoryId
    );
    documentItem.subItems = (documentItem?.subItems || []).filter(
      (sub) => sub.id !== subItem.id
    );

    const requestId = uuid();
    const isSoftDelete = checkDocumentItemUpdatedData(subItem as DocumentItem);
    const promises: Promise<any>[] = [
      documentItemApi.deleteSubItemList({
        ids: [subItem.id],
        isSoftDelete,
        requestId,
      }),
    ];

    const now = new Date();
    if (isSoftDelete) {
      promises.push(
        documentItemApi.updateItem({
          id: documentItem.id,
          overrideUpdatedAt: now,
          requestId,
        })
      );

      documentItem.updatedAt = now;
    }
    await Promise.all(promises);
    updateDocumentCategoriesWhenHidePhoto({
      documentCategories,
      documentItem,
      requestId,
    });

    insertItemLog({ field: "deleteSubItem" as any, requestId });

    setIsDeletingSubItem.off();
    socket.deleteSubItem(documentItem, subItem.id);

    dispatch(
      updateDocumentItem({ ...documentItem, isUpdateVisibleItem: true })
    );

    // add log type show/hide pin
    handleAddLogTypeShowHidePin({ documentCategory, documentItem, requestId });

    handleCloseConfirmDeleteItemModal();
    message.success(
      [
        "写真の削除",
        `${noSubItem} ${
          !!subItem?.title ? subItem?.title : ""
        }を削除しました。`,
      ],
      {
        duration: 2000,
      }
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    noSubItem,
    subItem,
    documentItemSelected,
    documentCategories,
    handleAddLogTypeShowHidePin,
    dispatch,
  ]);

  const handleOpenConfirmDeleteItemModal = useCallback(() => {
    setIsOpenConfirmDeleteItem.on();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCloseConfirmDeleteItemModal = useCallback(() => {
    setIsOpenConfirmDeleteItem.off();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Flex
      gap="0.8rem"
      height="5rem"
      alignItems="center"
      borderBottom="1px solid #D4D4D4"
      padding="0px .8rem"
      backgroundColor={isShowSubItem ? "transparent" : "#E0E0E0"}
    >
      <IconCamera
        flexShrink={0}
        width="2.4rem"
        height="2.4rem"
        cursor="pointer"
        onClick={() => {
          if (isDisabled && !isShowSubItem) return;
          onSelectSubItem(subItem);
        }}
        sx={{
          'path[data-name="stroke"]': {
            stroke: (subItem?.images as any)?.src ? "orange" : "#737373",
          },
        }}
        opacity={isShowSubItem ? "1" : "0.6"}
      />

      {isHasBlackboard && subItem?.isShowBlackboard !== false && (
        <IconBlackBoard
          flexShrink={0}
          width="2.4rem"
          height="2.4rem"
          cursor="pointer"
          onClick={() => {
            if (isDisabled && !isShowSubItem) return;
            onSelectSubItem(subItem);
          }}
        />
      )}

      <Text
        flexShrink={0}
        fontSize="1.4rem"
        userSelect="none"
        cursor="pointer"
        color="#737373"
        opacity={isShowSubItem ? "1" : "0.6"}
        onClick={() => {
          if (isDisabled && !isShowSubItem) return;
          onSelectSubItem(subItem);
        }}
      >
        {noSubItem}
      </Text>
      <Text
        noOfLines={1}
        flex={1}
        fontSize="1.6rem"
        color="#737373"
        opacity={isShowSubItem ? "1" : "0.6"}
        textDecoration={!isShowSubItem ? "line-through" : undefined}
        fontWeight="500"
        cursor="pointer"
        onClick={() => {
          if (isDisabled && !isShowSubItem) return;
          onSelectSubItem(subItem);
        }}
      >
        {subItem?.title || "-"}
      </Text>

      {!isShowSubItem && (
        <SvgIcon
          src="/img/eye-off.svg"
          cursor="pointer"
          width="2.4rem"
          height="2.4rem"
          pathFill="#737373"
          opacity="0.6"
          onClick={handleChangeShowSubItem}
        />
      )}

      {isShowSubItem && canDeleteDocumentItem && (
        <CustomMenu
          options={[
            {
              title: (
                <Flex gap="6px" alignItems="center">
                  <IconEye
                    flexShrink={0}
                    width="2.4rem"
                    height="2.4rem"
                    sx={{
                      path: {
                        fill: "#737373",
                      },
                    }}
                  />
                  <Text color="#737373" fontSize="1.2rem">
                    欠番 ON/OFF
                  </Text>
                </Flex>
              ) as any,
              onClick: handleChangeShowSubItem,
            },
            {
              title: (
                <Flex gap="6px" alignItems="center">
                  <IconTrash
                    flexShrink={0}
                    width="2.2rem"
                    height="2.2rem"
                    sx={{
                      path: {
                        fill: "font.danger",
                      },
                    }}
                  />
                  <Text fontSize="1.2rem" color="font.danger">
                    削除
                  </Text>
                </Flex>
              ) as any,
              isDisabled: !isOnline,
              onClick: handleOpenConfirmDeleteItemModal,
            },
          ]}
          icon={(isOpen) => (
            <Flex
              alignItems="center"
              justifyContent="center"
              width="2.4rem"
              height="2.4rem"
            >
              <IconThreeDot
                width="2.4rem"
                height="2.4rem"
                flexShrink="0"
                sx={{
                  path: {
                    fill: isOpen ? "var(--primary-color)" : "#A3A3A3",
                  },
                }}
              />
            </Flex>
          )}
        />
      )}

      {isOpenConfirmDeleteItem && (
        <ConfirmDeleteItemModal
          size="6xl"
          content={
            <Flex
              gap="0.8rem"
              height="5rem"
              alignItems="center"
              padding="0px .8rem"
            >
              <IconCamera
                flexShrink={0}
                width="2.4rem"
                height="2.4rem"
                sx={{
                  'path[data-name="stroke"]': {
                    stroke: (subItem?.images as any)?.src
                      ? "orange"
                      : "#737373",
                  },
                }}
              />
              <Text
                flexShrink={0}
                fontSize="1.4rem"
                userSelect="none"
                cursor="pointer"
                color="#737373"
                opacity={isShowSubItem ? "1" : "0.6"}
                onClick={() => {
                  onSelectSubItem(subItem);
                }}
              >
                {noSubItem}
              </Text>
              <Text
                noOfLines={1}
                flex={1}
                fontSize="1.6rem"
                color="#737373"
                fontWeight="500"
              >
                {subItem?.title || "-"}
              </Text>
            </Flex>
          }
          title="以下の写真を削除します。よろしいですか？"
          buttonDelete="削除する"
          buttonCancel="キャンセル"
          isOpen={isOpenConfirmDeleteItem}
          isLoading={isDeletingSubItem}
          onClose={handleCloseConfirmDeleteItemModal}
          onProcessing={handleDeleteSubItem}
        />
      )}
    </Flex>
  );
};

export default memo(SubItem);
